import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import updateLocale from 'dayjs/plugin/updateLocale';
import { formatDateByPrecision, getDateFormat, parseBareTime } from './dateDisplay';
import { markdownToPlainText } from './markdown';
import { allowedMeasures, formatMeasurement, isMeasurementValue, measureOfUnit } from './units';
import { formatCurrency, formatMajor, isCurrencyValue } from './currency';

// Default thresholds, plus an exact-seconds band (2–44s → "32 seconds") so
// fresh timestamps read precisely instead of "a few seconds".
dayjs.extend(relativeTime, {
	thresholds: [
		{ l: 's', r: 1 },
		{ l: 'ss', r: 44, d: 'second' },
		{ l: 'm', r: 89 },
		{ l: 'mm', r: 44, d: 'minute' },
		{ l: 'h', r: 89 },
		{ l: 'hh', r: 21, d: 'hour' },
		{ l: 'd', r: 35 },
		{ l: 'dd', r: 25, d: 'day' },
		{ l: 'M', r: 45 },
		{ l: 'MM', r: 10, d: 'month' },
		{ l: 'y', r: 17 },
		{ l: 'yy', d: 'year' },
	],
});
dayjs.extend(updateLocale);

// "3 days from now" instead of dayjs's default "in 3 days" — applies to every
// relative-time render (input suffixes and the display-template formatter).
dayjs.updateLocale('en', {
	relativeTime: {
		future: '%s from now',
		past: '%s ago',
		s: 'a few seconds',
		ss: '%d seconds',
		m: 'a minute',
		mm: '%d minutes',
		h: 'an hour',
		hh: '%d hours',
		d: 'a day',
		dd: '%d days',
		M: 'a month',
		MM: '%d months',
		y: 'a year',
		yy: '%d years',
	},
});

// Shared by the element-input relative suffix and the display-template
// 'relative' formatter. Empty string for blank/invalid values.
export const formatRelativeToNow = (value) => {
	if (value === undefined || value === null || value === '') return '';
	const parsed = dayjs(value);
	return parsed.isValid() ? parsed.fromNow() : '';
};

// The phone-width form of the same text: "3m ago", "2h ago", "5d ago",
// "3mo ago", "1y ago", "in 4h", "just now". Bands mirror the dayjs
// thresholds above so the two formats agree on the unit; only the words
// are shorter. Used for an input's suffix where a long "13 minutes ago"
// would push the typed value out of view. `now` is injectable for tests.
export const formatRelativeToNowCompact = (value, now = undefined) => {
	if (value === undefined || value === null || value === '') return '';
	const parsed = dayjs(value);
	if (!parsed.isValid()) return '';
	const ref = now === undefined ? dayjs() : dayjs(now);
	const diffSeconds = parsed.diff(ref, 'second');
	const s = Math.abs(diffSeconds);
	let text;
	if (s < 45) return 'just now';
	if (s < 90) text = '1m';
	else if (s < 45 * 60) text = `${Math.round(s / 60)}m`;
	else if (s < 90 * 60) text = '1h';
	else if (s < 22 * 3600) text = `${Math.round(s / 3600)}h`;
	else if (s < 36 * 3600) text = '1d';
	else if (s < 26 * 86400) text = `${Math.round(s / 86400)}d`;
	else {
		const months = Math.abs(parsed.diff(ref, 'month', true));
		if (months < 1.5) text = '1mo';
		else if (months < 11.5) text = `${Math.round(months)}mo`;
		else text = `${Math.max(1, Math.round(Math.abs(parsed.diff(ref, 'year', true))))}y`;
	}
	return diffSeconds < 0 ? `${text} ago` : `in ${text}`;
};

/**
 * The DAY-grained relative reading a plain `date` element gets: "today",
 * "yesterday", "tomorrow", "3 days ago", "2 months from now". A date has no
 * clock, so "7 hours ago" for today's date was the instant formatter
 * measuring from midnight — a number nobody entered. Whole calendar days
 * from the reader's local midnight, then the same month/year bands the
 * instant form uses. `now` is injectable for tests.
 */
export const formatRelativeDay = (value, now = undefined) => {
	if (value === undefined || value === null || value === '') return '';
	const parsed = dayjs(value);
	if (!parsed.isValid()) return '';
	const ref = (now === undefined ? dayjs() : dayjs(now)).startOf('day');
	const day = parsed.startOf('day');
	const days = day.diff(ref, 'day');
	if (days === 0) return 'today';
	if (days === 1) return 'tomorrow';
	if (days === -1) return 'yesterday';
	if (Math.abs(days) <= 25) return days < 0 ? `${-days} days ago` : `${days} days from now`;
	return day.from(ref);
};

/** The phone-width form: "today", "yesterday", "3d ago", "in 2mo". */
export const formatRelativeDayCompact = (value, now = undefined) => {
	if (value === undefined || value === null || value === '') return '';
	const parsed = dayjs(value);
	if (!parsed.isValid()) return '';
	const ref = (now === undefined ? dayjs() : dayjs(now)).startOf('day');
	const day = parsed.startOf('day');
	const days = day.diff(ref, 'day');
	if (days === 0) return 'today';
	if (days === 1) return 'tomorrow';
	if (days === -1) return 'yesterday';
	if (Math.abs(days) <= 25) return days < 0 ? `${-days}d ago` : `in ${days}d`;
	return formatRelativeToNowCompact(day, ref);
};

/**
 * The one entry point the input suffix and the display-template formatter
 * share: a `date` element reads in days, everything else (date-time, and the
 * created/updated stamps) as an instant.
 */
export const formatRelativeFor = (value, dataType, { compact = false, now = undefined } = {}) => {
	if (dataType === 'date') return compact ? formatRelativeDayCompact(value, now) : formatRelativeDay(value, now);
	return compact ? formatRelativeToNowCompact(value, now) : formatRelativeToNow(value);
};

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];

/**
 * Human file size, base 1024. Precision follows what a reader actually needs:
 * whole bytes and whole KB (nobody wants "35.2 KB"), one decimal from MB up
 * while the number is small enough for it to mean something ("3.0 MB",
 * "1.2 GB"), whole units past 10. A missing size is an em-dash, not "0 B" —
 * "we don't know" and "empty file" are different facts.
 */
export const formatBytes = (bytes) => {
	if (bytes === undefined || bytes === null || bytes === '') return '—';
	const num = Number(bytes);
	if (!Number.isFinite(num) || num < 0) return '—';
	if (num < 1024) return `${Math.round(num)} B`;

	let value = num;
	let unit = 0;
	while (value >= 1024 && unit < BYTE_UNITS.length - 1) {
		value /= 1024;
		unit += 1;
	}
	// KB stays whole; MB and up get a decimal only while under 10.
	const decimals = unit >= 2 && value < 10 ? 1 : 0;
	return `${value.toFixed(decimals)} ${BYTE_UNITS[unit]}`;
};

const ensureString = (val) => {
	if (val === undefined || val === null) return '';
	if (typeof val === 'string') return val;
	return String(val);
};

const tryDayjs = (value) => {
	const parsed = dayjs(value);
	return parsed.isValid() ? parsed : null;
};

// A `time` element stores 'HH:mm', which dayjs does not read — so the ONE
// formatter that prints a clock anchors a bare one to an arbitrary day and
// keeps only the clock half. Deliberately not folded into `tryDayjs`: the
// other date formatters must leave a bare clock alone, exactly as the API's
// port of this registry does (utils/displayTitle.js `tryDayjsTime`), or the
// same template renders '13:45' here and "1970"/"57 years ago" there — and
// the API is the one that writes the denormalized title.
const TIME_ANCHOR = '1970-01-01';

const tryDayjsTime = (value) => parseBareTime(value, TIME_ANCHOR) || tryDayjs(value);

// Measurement is deliberately NOT in NUMBER_TYPES: the number formatters
// would Number({}) a stored object. A measurement token renders through the
// shared formatter (the element's display rules) unless the template asks
// for a specific unit with `unit(...)`. Mirrors keepr-api utils/displayTitle.js.
// Boolean is deliberately in NO set: a yes/no renders as the element's own
// two labels (itemValueDisplay formatValue), and every formatter here would
// either stringify it wrongly (upper -> "TRUE") or Number() it. Its menu is
// therefore Default + Fallback, which is all a two-state value needs.
const NUMBER_TYPES = ['number', 'decimal', 'integer', 'rating'];
// `phone`, `email` and `location` ARE text for the formatter registry: every
// text formatter (truncate, substring, replace, initials) is meaningful on a
// phone number, an address or an address book entry. They enter the chain as
// the text the platform DISPLAYS (utils/displayTemplate templateTokenInput),
// not as the shape it stores, so `substring` cuts what the reader sees rather
// than an E.164 prefix or the characters of '[object Object]'.
// A `color` is its "#rrggbb" — a title is text, so it shows the hex.
const TEXT_TYPES = ['text-small', 'text-large', 'rich-text', 'choice', 'url', 'phone', 'email', 'location', 'user', 'color'];
const DATE_TYPES = ['date', 'date-time', 'time'];
const MEASUREMENT_TYPES = ['measurement'];

/** A measurement value as the element renders it ('' when unreadable). */
export const formatMeasurementValue = (value, options) => formatMeasurement(value, options || {});

// Currency is in NO set either, for the measurement reason: a stored
// { amount, currency } object would Number() to NaN. A currency token enters
// the chain already formatted ("$1,234.50" -- utils/displayTemplate
// templateTokenInput), so its menu is Default + Fallback. Mirrors keepr-api
// utils/displayTitle.js.
/** A currency value as the element renders it ('' when unreadable). */
export const formatCurrencyValue = (value, options) => formatCurrency(value, options || {});

// Formatter registry — the single source of truth for display-template
// functions. `types` restricts which data types a formatter APPLIES to at
// render time (undefined = any). `menuTypes` limits which data types OFFER
// the formatter in the editor menu without restricting render behavior
// (used to keep legacy templates rendering while decluttering menus).
const registry = {
	date: {
		label: 'Date',
		description: 'Format as date string',
		types: DATE_TYPES,
		args: [
			{ name: 'format', label: 'Format', type: 'text', placeholder: 'MMM D, YYYY', optional: true },
		],
		transform: (value, args, context) => {
			const parsed = tryDayjs(value);
			if (!parsed) return value;
			// With no pattern of its own, a date token reads at the ELEMENT's
			// precision — a month-precision element renders "September 2026"
			// in a title exactly as it does in a cell. An explicit pattern is
			// the template author's word and wins over both.
			if (!args[0]) {
				const precision = context?.options?.precision;
				if (context?.dataType === 'date' && (precision === 'month' || precision === 'year')) {
					return formatDateByPrecision(value, precision);
				}
			}
			const format = args[0] || getDateFormat();
			return parsed.format(format);
		}
	},
	time: {
		label: 'Time',
		description: 'Format as time string',
		types: DATE_TYPES,
		args: [
			{ name: 'format', label: 'Format', type: 'text', placeholder: 'h:mm A', defaultValue: 'h:mm A' },
		],
		transform: (value, args) => {
			// The one formatter that reads a `time` element's own value, so
			// the one that must know both stored shapes (the API's port says
			// the same thing in the same place).
			const parsed = tryDayjsTime(value);
			if (!parsed) return value;
			// Deliberately NOT the timeFormat preference. This arg carries a
			// stored defaultValue, so every template already spells its own
			// pattern and any fallback here is unreachable; making the display
			// template follow the clock preference is a display-template
			// decision, not a preferences one.
			const format = args[0] || 'h:mm A';
			return parsed.format(format);
		}
	},
	year: {
		label: 'Year',
		description: 'Extract year component',
		types: DATE_TYPES,
		args: [],
		transform: (value) => {
			const parsed = tryDayjs(value);
			return parsed ? parsed.format('YYYY') : value;
		}
	},
	monthYear: {
		label: 'Month & Year',
		description: 'Format as month + year string',
		types: DATE_TYPES,
		args: [
			{ name: 'format', label: 'Format', type: 'text', placeholder: 'MMM YYYY', defaultValue: 'MMM YYYY' },
		],
		transform: (value, args) => {
			const parsed = tryDayjs(value);
			if (!parsed) return value;
			const format = args[0] || 'MMM YYYY';
			return parsed.format(format);
		}
	},
	weekday: {
		label: 'Weekday',
		description: 'Day of the week (e.g. Monday)',
		types: DATE_TYPES,
		args: [],
		transform: (value) => {
			const parsed = tryDayjs(value);
			return parsed ? parsed.format('dddd') : value;
		}
	},
	quarter: {
		label: 'Quarter',
		description: 'Calendar quarter (e.g. Q3)',
		types: DATE_TYPES,
		args: [],
		transform: (value) => {
			const parsed = tryDayjs(value);
			return parsed ? `Q${Math.ceil((parsed.month() + 1) / 3)}` : value;
		}
	},
	isoDate: {
		label: 'ISO date',
		description: 'Format as YYYY-MM-DD',
		types: DATE_TYPES,
		args: [],
		transform: (value) => {
			const parsed = tryDayjs(value);
			return parsed ? parsed.format('YYYY-MM-DD') : value;
		}
	},
	relative: {
		label: 'Relative time',
		description: 'Relative to now (e.g. 3 days ago; a date reads in whole days: today, yesterday)',
		types: DATE_TYPES,
		args: [],
		transform: (value, args, ctx) => {
			const parsed = tryDayjs(value);
			if (!parsed) return value;
			// A date is a day, not an instant: today's date is "today", never
			// "7 hours ago" (the API's displayTitle.js says the same).
			return ctx?.dataType === 'date' ? formatRelativeDay(parsed) : parsed.fromNow();
		}
	},
	age: {
		label: 'Age (years)',
		description: 'Whole years elapsed since the date',
		types: DATE_TYPES,
		args: [],
		transform: (value) => {
			const parsed = tryDayjs(value);
			if (!parsed) return value;
			return String(Math.max(0, dayjs().diff(parsed, 'year')));
		}
	},
	initial: {
		label: 'First initial',
		description: 'First letter capitalized',
		types: TEXT_TYPES,
		args: [],
		transform: (value) => {
			const str = ensureString(value).trim();
			return str ? str.charAt(0).toUpperCase() : '';
		}
	},
	initials: {
		label: 'Initials',
		description: 'Initials from each word',
		types: TEXT_TYPES,
		args: [],
		transform: (value) => {
			const str = ensureString(value).trim();
			if (!str) return '';
			return str
				.split(/\s+/)
				.filter(Boolean)
				.map(word => word.charAt(0).toUpperCase())
				.join('');
		}
	},
	substring: {
		label: 'Substring',
		description: 'Take a slice of the text',
		types: TEXT_TYPES,
		args: [
			{ name: 'start', label: 'Start index', type: 'number', defaultValue: 0 },
			{ name: 'length', label: 'Length', type: 'number', optional: true },
		],
		transform: (value, args) => {
			const str = ensureString(value);
			const start = Number(args[0] ?? 0);
			const length = args[1] !== undefined && args[1] !== null && args[1] !== '' ? Number(args[1]) : undefined;
			if (Number.isNaN(start)) return str;
			if (length === undefined || Number.isNaN(length)) {
				return str.slice(start);
			}
			return str.slice(start, start + length);
		}
	},
	upper: {
		label: 'Uppercase',
		description: 'Convert to uppercase',
		menuTypes: TEXT_TYPES,
		args: [],
		transform: (value) => ensureString(value).toUpperCase(),
	},
	lower: {
		label: 'Lowercase',
		description: 'Convert to lowercase',
		menuTypes: TEXT_TYPES,
		args: [],
		transform: (value) => ensureString(value).toLowerCase(),
	},
	capitalize: {
		label: 'Title case',
		description: 'Capitalize each word',
		types: TEXT_TYPES,
		args: [],
		transform: (value) => ensureString(value)
			.toLowerCase()
			.replace(/(^|\s)\S/g, (c) => c.toUpperCase()),
	},
	plainText: {
		label: 'Plain text',
		description: 'Strip rich-text formatting, keep the words',
		menuTypes: ['rich-text'],
		args: [],
		transform: (value) => markdownToPlainText(ensureString(value)),
	},
	trim: {
		label: 'Trim',
		description: 'Remove surrounding whitespace',
		types: TEXT_TYPES,
		args: [],
		transform: (value) => ensureString(value).trim(),
	},
	truncate: {
		label: 'Truncate',
		description: 'Limit length with an ellipsis',
		types: TEXT_TYPES,
		args: [
			{ name: 'length', label: 'Max characters', type: 'number', defaultValue: 24 },
			{ name: 'suffix', label: 'Suffix', type: 'text', placeholder: '…', defaultValue: '…', optional: true },
		],
		transform: (value, args) => {
			const str = ensureString(value);
			const max = Number(args[0] ?? 24);
			if (Number.isNaN(max) || max <= 0 || str.length <= max) return str;
			const suffix = args[1] !== undefined && args[1] !== null ? String(args[1]) : '…';
			return str.slice(0, max).trimEnd() + suffix;
		}
	},
	replace: {
		label: 'Replace',
		description: 'Replace all occurrences of text',
		types: TEXT_TYPES,
		args: [
			{ name: 'search', label: 'Find', type: 'text', defaultValue: '' },
			{ name: 'replacement', label: 'Replace with', type: 'text', defaultValue: '', optional: true },
		],
		transform: (value, args) => {
			const str = ensureString(value);
			const search = args[0] !== undefined && args[0] !== null ? String(args[0]) : '';
			if (!search) return str;
			const replacement = args[1] !== undefined && args[1] !== null ? String(args[1]) : '';
			return str.split(search).join(replacement);
		}
	},
	fallback: {
		label: 'Fallback',
		description: 'Show substitute text when the value is empty',
		args: [
			{ name: 'text', label: 'Fallback text', type: 'text', defaultValue: '' },
		],
		transform: (value, args) => {
			const isEmpty = value === undefined || value === null || ensureString(value).trim() === '';
			return isEmpty ? (args[0] ?? '') : value;
		}
	},
	round: {
		label: 'Round number',
		description: 'Round to decimal places',
		types: NUMBER_TYPES,
		args: [
			{ name: 'precision', label: 'Decimals', type: 'number', defaultValue: 0 },
		],
		transform: (value, args) => {
			const num = Number(value);
			if (Number.isNaN(num)) return value;
			const precision = Number(args[0] ?? 0);
			if (Number.isNaN(precision)) return num;
			const factor = 10 ** precision;
			const rounded = Math.round(num * factor) / factor;
			return precision > 0 ? rounded.toFixed(Math.max(0, precision)) : String(rounded);
		}
	},
	thousands: {
		label: 'Thousands separator',
		description: 'Group digits (e.g. 12,345)',
		types: NUMBER_TYPES,
		args: [],
		transform: (value) => {
			const num = Number(value);
			if (Number.isNaN(num)) return value;
			return num.toLocaleString();
		}
	},
	currency: {
		label: 'Currency',
		description: 'Format as money (e.g. $1,234.50)',
		types: NUMBER_TYPES,
		args: [
			{ name: 'code', label: 'Currency code', type: 'text', placeholder: 'USD', defaultValue: 'USD' },
		],
		transform: (value, args) => {
			const num = Number(value);
			if (Number.isNaN(num)) return value;
			const code = (args[0] || 'USD').toString().toUpperCase();
			// The shared currency module, not Intl: Intl's output depends on
			// the browser's locale and CLDR release, and the API prints the
			// same title through the same function (docs/plans/currency.md
			// decision 5). An unknown code leaves the value as it was.
			return formatMajor(num, code) || value;
		}
	},
	percent: {
		label: 'Percent',
		description: 'Show a 0–1 fraction as a percentage (0.5 → 50%)',
		types: NUMBER_TYPES,
		args: [
			{ name: 'decimals', label: 'Decimals', type: 'number', defaultValue: 0 },
		],
		transform: (value, args) => {
			const num = Number(value);
			if (Number.isNaN(num)) return value;
			const decimals = Math.max(0, Number(args[0] ?? 0) || 0);
			return `${(num * 100).toFixed(decimals)}%`;
		}
	},
	compact: {
		label: 'Compact number',
		description: 'Abbreviate large numbers (e.g. 1.2K)',
		types: NUMBER_TYPES,
		args: [],
		transform: (value) => {
			const num = Number(value);
			if (Number.isNaN(num)) return value;
			return new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(num);
		}
	},
	ordinal: {
		label: 'Ordinal',
		description: 'Add ordinal suffix (1st, 2nd, 3rd…)',
		types: NUMBER_TYPES,
		args: [],
		transform: (value) => {
			const num = Number(value);
			if (Number.isNaN(num) || !Number.isFinite(num)) return value;
			const n = Math.trunc(Math.abs(num));
			const mod100 = n % 100;
			const mod10 = n % 10;
			const suffix = (mod100 >= 11 && mod100 <= 13) ? 'th'
				: mod10 === 1 ? 'st'
				: mod10 === 2 ? 'nd'
				: mod10 === 3 ? 'rd'
				: 'th';
			return `${num}${suffix}`;
		}
	},
	abs: {
		label: 'Absolute value',
		description: 'Drop the sign of the number',
		types: NUMBER_TYPES,
		args: [],
		transform: (value) => {
			const num = Number(value);
			return Number.isNaN(num) ? value : String(Math.abs(num));
		}
	},
	// "Weight in kg, 1 decimal": renders a measurement in a named unit (an id
	// of the element's measure) at a precision, overriding the element's
	// display rules. Either arg may be omitted (the element's own display
	// unit / precision then applies). A unit of another measure is ignored.
	unit: {
		label: 'Unit',
		description: 'Show in a chosen unit and number of decimals',
		types: MEASUREMENT_TYPES,
		args: [
			{ name: 'unit', label: 'Unit', type: 'text', placeholder: 'kg', optional: true },
			{ name: 'precision', label: 'Decimals', type: 'number', placeholder: '1', optional: true },
		],
		transform: (value, args, meta) => {
			const options = (meta && meta.options) || {};
			const unitId = typeof args[0] === 'string' && args[0].trim() ? args[0].trim() : undefined;
			const precisionRaw = args[1] === undefined || args[1] === null || args[1] === '' ? NaN : Number(args[1]);
			const precision = Number.isInteger(precisionRaw) ? precisionRaw : undefined;
			const prefs = {};
			// A unit of any measure the element allows (KPR-1085), as the API's
			// title formatter reads it; one of another measure is ignored.
			if (unitId && allowedMeasures(options).includes(measureOfUnit(unitId))) prefs.unit = unitId;
			if (precision !== undefined) prefs.precision = precision;
			const out = formatMeasurement(value, options, prefs);
			return out || formatMeasurementValue(value, options);
		}
	},
};

export const availableFormatters = Object.entries(registry).map(([key, meta]) => ({
	name: key,
	label: meta.label || key,
	description: meta.description || '',
	types: meta.types || null,
	menuTypes: meta.menuTypes || meta.types || null,
	args: meta.args || [],
}));

export const getFormatterMeta = (name) => {
	if (!name) return null;
	return registry[name] || null;
};

const formatterSupportsType = (meta, dataType) => {
	if (!meta?.types || !meta.types.length) return true;
	if (!dataType) return true;
	return meta.types.includes(dataType);
};

const normalizeArgs = (meta, args = []) => {
	const defs = meta?.args || [];
	return defs.map((def, idx) => {
		const val = args[idx];
		if (val === undefined || val === null || val === '') {
			return def.defaultValue !== undefined ? def.defaultValue : '';
		}
		return val;
	});
};

/**
 * applyFormatters(value, dataType, formatSpecs, options) — `options` is the
 * element's options; only the measurement formatter reads it (the measure
 * decides which units a `unit(...)` argument may name).
 */
export function applyFormatters(value, dataType, formatSpecs = [], options = undefined) {
	// A date element may hold SEVERAL dates (utils/dateOptions
	// `allowMultiple`). Every formatter here takes ONE date, so the chain runs
	// per entry and the readings are joined — `date`, `year`, `relative` all
	// mean the same thing about each of three doses as they do about one.
	// Only the date family — and a Choice that allows multiple values (data
	// types PR 5), whose entries read one at a time the same way ("welding,
	// rigging"); keepr-api utils/displayTitle.js applyFormatters says the
	// same. An array anywhere else is a card lookup's list of ids, which the
	// text formatters would happily mangle.
	if (Array.isArray(value) && (DATE_TYPES.includes(dataType) || dataType === 'choice')) {
		return value
			.map(entry => applyFormatters(entry, dataType, formatSpecs, options))
			.map(entry => (entry === undefined || entry === null ? '' : String(entry)))
			.filter(entry => entry !== '')
			.join(', ');
	}
	let output = value;
	for (const spec of formatSpecs || []) {
		if (!spec?.name) continue;
		const formatter = registry[spec.name];
		if (!formatter) continue;
		if (!formatterSupportsType(formatter, dataType)) continue;
		try {
			const args = normalizeArgs(formatter, spec.args);
			output = formatter.transform(output, args, { dataType, options });
		} catch (e) {
			// ignore formatter failure
		}
	}
	// A measurement no formatter consumed (or that `unit` left as an object)
	// still renders as its formatted string, never "[object Object]".
	if (dataType === 'measurement' && (isMeasurementValue(output) || typeof output === 'number')) {
		return formatMeasurementValue(output, options);
	}
	// The same guard for money: a stored object is never "[object Object]".
	if (dataType === 'currency' && (isCurrencyValue(output) || typeof output === 'number')) {
		return formatCurrencyValue(output, options);
	}
	return output;
}
